import React from "react";
import { useState, createRef, useEffect } from "react";

// MATERIAL UI
import { makeStyles } from "@material-ui/core/styles";
import { ChevronLeft as IconChevronLeft } from "@material-ui/icons";

type Props = {
  /** Panel can resize vertically or horizontally */
  direction: "vertical" | "horizontal";
  /** The content for the first container */
  firstContent: any;
  /** The content for the second container */
  secondContent: any;
  /** Initial width or height of first container */
  firstInit?: string | number;
  /** Minimum width or height of first container */
  firstMin?: number;
  /** Maximum width or height of first container */
  firstMax?: number;
  /** Minimum width or height of second container */
  secondMin?: number;
  /** Maximum width or height of second container */
  secondMax?: number;
  firstCollapsible?: boolean;
  secondCollapsible?: boolean;
  firstName?: string;
  secondName?: string;
  onResize?: () => void;
};

const COLLAPSED_SIZE = 10;
const commonPanel = {
  display: "flex",
  flexDirection: "column",
  position: "relative",
};

const useStyles = makeStyles(
  () =>
    ({
      root: {
        display: "flex",
        width: "100%",
        height: "100%",
        flexGrow: 1,
        "&.vertical": {
          flexDirection: "column",
        },
      },
      firstPanel: {
        ...commonPanel,
        "&.horizontal:not(.collapsed)": {
          minWidth: ({ firstMin }) => firstMin,
          maxWidth: ({ firstMax }) => firstMax,
        },
        "&.vertical:not(.collapsed)": {
          minHeight: ({ firstMin }) => firstMin,
          maxHeight: ({ firstMax }) => firstMax,
        },
      },
      secondPanel: {
        ...commonPanel,
        "&.horizontal:not(.collapsed)": {
          minWidth: ({ secondMin }) => secondMin,
          maxWidth: ({ secondMax }) => secondMax,
        },
        "&.vertical:not(.collapsed)": {
          minHeight: ({ secondMin }) => secondMin,
          maxHeight: ({ secondMax }) => secondMax,
        },
      },

      divider: {
        transitionDuration: "500ms",
        position: "absolute",
        zIndex: 10,
        opacity: 0.3,

        "& .collapseFirst, .collapseSecond": {
          transitionDelay: "500ms",
          transitionDuration: "500ms",
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          width: 40,
          height: 40,
          opacity: 0,
          transform: "scale(0)",
          cursor: "pointer",
          "& > svg": {
            borderRadius: "50%",
            backgroundColor: "white",
            boxShadow: "1px 0px 2px black, 0px 0px 10px black",
          },
        },
        "&:hover": {
          opacity: 1,
          "& .collapseFirst, .collapseSecond": {
            transitionDelay: "500ms",
            opacity: 1,
            transform: "scale(1)",
          },
        },

        "&.horizontal": {
          right: -10,
          top: 0,
          width: 20,
          height: "100%",
          background:
            "linear-gradient(to right, transparent, transparent 10px, #000 10px,transparent 11px, transparent), linear-gradient(to right, transparent, rgba(255,255,255,0.3) 7px, white 10px, rgba(255,255,255,0.3) 13px, transparent)",
          "&:not(.collapsed)": { cursor: "col-resize" },

          "& .collapseFirst": {
            left: -30,
          },
          "& .collapseSecond": {
            left: 10,
            "& > svg": {
              transform: "rotate(180deg)",
            },
          },
        },

        "&.vertical": {
          right: 0,
          bottom: -10,
          height: 20,
          width: "100%",
          background:
            "linear-gradient(to bottom, transparent, transparent 10px, #000 11px,transparent 11px, transparent), linear-gradient(to bottom, transparent, rgba(255,255,255,0.3) 7px, white 10px, rgba(255,255,255,0.3) 13px, transparent)",
          "&:not(.collapsed)": { cursor: "row-resize" },

          "& .collapseFirst": {
            top: -30,
            "& > svg": {
              transform: "rotate(90deg)",
            },
          },
          "& .collapseSecond": {
            top: 10,
            "& > svg": {
              transform: "rotate(270deg)",
            },
          },
        },
      },
    } as any)
);

type ResizeTriggerProps = {
  event: React.MouseEvent<HTMLDivElement, MouseEvent>;
  direction: string;
  refRoot: React.RefObject<HTMLDivElement>;
  refFirst: React.RefObject<HTMLDivElement>;
  refSecond: React.RefObject<HTMLDivElement>;
  onResize?: () => void;
};

function triggerResizing({
  event,
  direction,
  refFirst,
  refRoot,
  refSecond,
  onResize,
}: ResizeTriggerProps) {
  event.preventDefault();
  const elemFirst = refFirst.current;
  const elemRoot = refRoot.current;
  const elemSecond = refSecond.current;
  let mouseLeftPrev = event.clientX;
  let mouseTopPrev = event.clientY;

  // Set mouse event handlers on document:
  document.onmousemove = devent => {
    devent.preventDefault();
    const dx = mouseLeftPrev - devent.clientX;
    const dy = mouseTopPrev - devent.clientY;
    mouseLeftPrev = devent.clientX;
    mouseTopPrev = devent.clientY;

    if (elemFirst && elemRoot && elemSecond) {
      switch (direction) {
        case "horizontal":
          {
            const firstWidth = elemFirst.clientWidth - dx;
            elemFirst.style.width = firstWidth + "px";
            elemSecond.style.width = elemRoot.clientWidth - firstWidth + "px";
          }
          break;

        case "vertical":
          {
            const firstHeight = elemFirst.clientHeight - dy;
            elemFirst.style.height = firstHeight + "px";
            elemSecond.style.height = elemRoot.clientHeight - firstHeight + "px";
          }
          break;

        default:
          break;
      }
    }
  };
  document.onmouseup = () => {
    document.onmouseup = null;
    document.onmousemove = null;
    onResize && onResize();
  };
}

function ResizableSplitPanel({
  direction,
  firstContent,
  secondContent,
  firstInit,
  firstMin,
  firstMax,
  secondMin,
  secondMax,
  firstCollapsible = true,
  secondCollapsible = true,
  firstName = "panel",
  secondName = "panel",
  onResize,
}: Props) {
  const classes: any = useStyles({ firstMin, firstMax, secondMin, secondMax });
  const refRoot = createRef<HTMLDivElement>();
  const refFirst = createRef<HTMLDivElement>();
  const refSecond = createRef<HTMLDivElement>();

  // A positive value indicates the panel is collapsed, and the number is the previous size:
  const [firstCollapsedPrev, setFirstCollapsedPrev] = useState(0);
  const [secondCollapsedPrev, setSecondCollapsedPrev] = useState(0);

  // The property names based on direction:
  const styleProp = (direction == "horizontal" && "width") || "height";
  const clientSize = (direction == "horizontal" && "clientWidth") || "clientHeight";

  function collapseFirst() {
    let prev = 0;
    if (!firstCollapsedPrev && refSecond.current && refRoot.current && refFirst.current) {
      prev = refFirst.current[clientSize];
      refFirst.current.style[styleProp] = COLLAPSED_SIZE + "px";
      refSecond.current.style[styleProp] = refRoot.current[clientSize] - COLLAPSED_SIZE + "px";
    }
    setFirstCollapsedPrev(prev);
    onResize && onResize();
  }

  function collapseSecond() {
    let prev = 0;
    if (!secondCollapsedPrev && refSecond.current && refRoot.current && refFirst.current) {
      prev = refSecond.current[clientSize];
      refSecond.current.style[styleProp] = COLLAPSED_SIZE + "px";
      refFirst.current.style[styleProp] = refRoot.current[clientSize] - COLLAPSED_SIZE + "px";
    }
    setSecondCollapsedPrev(prev);
    onResize && onResize();
  }

  function restoreFirst() {
    if (refSecond.current && refRoot.current && refFirst.current) {
      refFirst.current.style[styleProp] = firstCollapsedPrev + "px";
      refSecond.current.style[styleProp] = refRoot.current[clientSize] - firstCollapsedPrev + "px";
    }
    setFirstCollapsedPrev(0);
    onResize && onResize();
  }

  function restoreSecond() {
    if (refSecond.current && refRoot.current && refFirst.current) {
      refSecond.current.style[styleProp] = secondCollapsedPrev + "px";
      refFirst.current.style[styleProp] = refRoot.current[clientSize] - secondCollapsedPrev + "px";
    }
    setSecondCollapsedPrev(0);
    onResize && onResize();
  }

  // set the initial sizes based on the initial size of first container, or 50-50
  useEffect(() => {
    // these are necessarily true when this runs, but typescript doesn't know that
    if (refFirst.current && refRoot.current && refSecond.current) {
      const elemRoot = refRoot.current;
      const elemFirst = refFirst.current;
      const elemSecond = refSecond.current;
      const rootSize = elemRoot[clientSize];
      let firstSize = rootSize / 2;
      if (firstInit) {
        if (firstInit.constructor == String && firstInit.indexOf("%") !== -1) {
          firstSize = (Number(firstInit.replace("%", "")) / 100) * rootSize;
        } else {
          firstSize = Number(firstInit);
        }
      }
      elemFirst.style[styleProp] = firstSize + "px";
      elemSecond.style[styleProp] = rootSize - firstSize + "px";

      const onWindowResize = () => {
        // if either panel is collapsed, just change size of other panel
        const firstCollapsed = elemFirst[clientSize] == COLLAPSED_SIZE;
        const secondCollapsed = elemSecond[clientSize] == COLLAPSED_SIZE;
        if (firstCollapsed) {
          elemSecond.style[styleProp] = elemRoot[clientSize] - COLLAPSED_SIZE + "px";
        } else if (secondCollapsed) {
          elemFirst.style[styleProp] = elemRoot[clientSize] - COLLAPSED_SIZE + "px";
        } else {
          // use initial width% of first panel to preserve ratio:
          const firstPercent =
            elemFirst[clientSize] / (elemFirst[clientSize] + elemSecond[clientSize]);
          elemFirst.style[styleProp] = elemRoot[clientSize] * firstPercent + "px";
          elemSecond.style[styleProp] = elemRoot[clientSize] * (1 - firstPercent) + "px";
        }
        onResize && onResize();
      };

      window.addEventListener("resize", onWindowResize);

      // Unmount cleanup:
      return () => {
        window.removeEventListener("resize", onWindowResize);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // the empty array is actually needed to replicate "componentDidMount" behavior

  return (
    <div ref={refRoot} className={`${classes.root} ${direction}`}>
      <div
        ref={refFirst}
        className={`${classes.firstPanel} ${direction} ${
          (firstCollapsedPrev && "collapsed") || ""
        }`}
      >
        {firstContent}
        <div
          className={`${classes.divider} ${direction} ${
            ((secondCollapsedPrev || firstCollapsedPrev) && "collapsed") || ""
          }`}
          onMouseDown={
            secondCollapsedPrev
              ? restoreSecond
              : firstCollapsedPrev
              ? restoreFirst
              : event =>
                  triggerResizing({ event, direction, refRoot, refFirst, refSecond, onResize }) // only resize if neither collapsed
          }
        >
          {!firstCollapsedPrev && firstCollapsible && (
            <div
              className="collapseFirst"
              title={
                secondCollapsedPrev
                  ? `Restore ${direction == "vertical" ? "bottom" : "right"} ${secondName}`
                  : `Collapse ${direction == "vertical" ? "top" : "left"} ${firstName}`
              }
              onClick={secondCollapsedPrev ? restoreSecond : collapseFirst}
            >
              <IconChevronLeft />
            </div>
          )}
          {!secondCollapsedPrev && secondCollapsible && (
            <div
              className="collapseSecond"
              title={
                firstCollapsedPrev
                  ? `Restore ${direction == "vertical" ? "top" : "left"} ${secondName}`
                  : `Collapse ${direction == "vertical" ? "bottom" : "right"} ${firstName}`
              }
              onClick={firstCollapsedPrev ? restoreFirst : collapseSecond}
            >
              <IconChevronLeft />
            </div>
          )}
        </div>
      </div>
      <div
        ref={refSecond}
        className={`${classes.secondPanel} ${direction} ${
          (secondCollapsedPrev && "collapsed") || ""
        }`}
      >
        {secondContent}
      </div>
    </div>
  );
}

export default ResizableSplitPanel;
